import React, { useMemo, useRef, useState, useEffect, useContext } from "react";
import { EditCtx, useField, Logo, EditText, EditImage, Browser } from "./templates.jsx";

// Enable Healthcare — Additional image-forward templates
// Loaded after templates.jsx; adds: FullBleedImage, SplitImageText,
// PhotoStack, SuccessStory, ProductMockup, WebinarBig, MagazineCover,
// ImageQuoteOverlay, ImageStat, BeforeAfter, FeatureGrid, BannerImage.

function FullBleedImage({ size, defaults }) {
  // Image fills entire canvas with overlay gradient and text on top
  const L = {
    square:   { pad: 80,  h: 132, eyebrow: 22, logoH: 48 },
    portrait: { pad: 80,  h: 148, eyebrow: 24, logoH: 48 },
    story:    { pad: 100, h: 176, eyebrow: 26, logoH: 60 },
    wide:     { pad: 56,  h: 76,  eyebrow: 16, logoH: 36 },
    yt:       { pad: 100, h: 144, eyebrow: 22, logoH: 48 },
  }[size];
  return (
    <div className={`canvas size-${size}`} style={{ background:"#0A1628" }}>
      <EditImage fieldKey="image" defaultSrc={defaults.image} label="Full-bleed image"
        style={{ position:"absolute", inset:0, borderRadius:0 }}/>
      <div style={{
        position:"absolute", inset:0,
        background:"linear-gradient(180deg, rgba(10,22,40,0.15) 0%, rgba(10,22,40,0.55) 60%, rgba(10,22,40,0.92) 100%)",
        pointerEvents:"none"
      }}/>
      <div style={{ position:"absolute", inset: L.pad, display:"flex", flexDirection:"column", justifyContent:"space-between", color:"white" }}>
        <Logo variant="lockup" color="white" height={L.logoH}/>
        <div style={{ display:"flex", flexDirection:"column", gap: 24 }}>
          <EditText fieldKey="eyebrow" fallback={defaults.eyebrow} as="div" className="meta-text"
            style={{ fontSize: L.eyebrow, color:"rgba(255,255,255,0.78)", fontWeight: 500 }}/>
          <EditText fieldKey="headline" fallback={defaults.headline} as="h1" multiline
            style={{ margin:0, fontSize: L.h, fontWeight: 700, lineHeight: 0.95, letterSpacing:"-0.035em", color:"white", textWrap:"balance" }}/>
          <EditText fieldKey="cta" fallback={defaults.cta} as="div"
            style={{ fontSize: L.eyebrow*1.05, color:"white", fontWeight: 500, marginTop: 4 }}/>
        </div>
      </div>
    </div>
  );
}

function SplitImageText({ size, defaults, reverse = false }) {
  // Half image, half navy block with type
  const isVertical = size === "story" || size === "portrait";
  const L = {
    square:   { pad: 72, h: 110, eyebrow: 20, logoH: 44 },
    portrait: { pad: 72, h: 124, eyebrow: 22, logoH: 44 },
    story:    { pad: 96, h: 156, eyebrow: 24, logoH: 56 },
    wide:     { pad: 56, h: 64,  eyebrow: 14, logoH: 32 },
    yt:       { pad: 96, h: 124, eyebrow: 20, logoH: 44 },
  }[size];

  const ImagePane = (
    <EditImage fieldKey="image" defaultSrc={defaults.image} label="Editorial photo" style={{ height:"100%", borderRadius:0 }}/>
  );
  const TextPane = (
    <div className="bg-navy-gradient" style={{ padding: L.pad, display:"flex", flexDirection:"column", justifyContent:"space-between", color:"white" }}>
      <Logo variant="lockup" color="white" height={L.logoH}/>
      <div style={{ display:"flex", flexDirection:"column", gap: 22 }}>
        <EditText fieldKey="eyebrow" fallback={defaults.eyebrow} as="div" className="meta-text"
          style={{ fontSize: L.eyebrow, color:"rgba(255,255,255,0.7)", fontWeight: 500 }}/>
        <EditText fieldKey="headline" fallback={defaults.headline} as="h1" multiline
          style={{ margin:0, fontSize: L.h, fontWeight: 700, lineHeight: 0.96, letterSpacing:"-0.035em", color:"white", textWrap:"balance" }}/>
        <EditText fieldKey="support" fallback={defaults.support} as="p" multiline
          style={{ margin:0, fontSize: L.eyebrow*1.05, lineHeight: 1.4, color:"rgba(255,255,255,0.78)", letterSpacing:"-0.01em" }}/>
      </div>
      <EditText fieldKey="cta" fallback={defaults.cta} as="div"
        style={{ fontSize: L.eyebrow, fontWeight: 500, color:"rgba(255,255,255,0.92)" }}/>
    </div>
  );

  const grid = isVertical
    ? { gridTemplateRows: "1fr 1fr" }
    : { gridTemplateColumns: reverse ? "1fr 1fr" : "1fr 1fr" };

  return (
    <div className={`canvas size-${size}`} style={{ background:"#0A1628" }}>
      <div style={{ position:"absolute", inset:0, display:"grid", ...grid }}>
        {reverse ? <>{TextPane}{ImagePane}</> : <>{ImagePane}{TextPane}</>}
      </div>
    </div>
  );
}

function SuccessStory({ size, defaults }) {
  // Client photo + name/practice + headline result + stat
  const L = {
    square:   { pad: 72, h: 88,  eyebrow: 18, body: 22, logoH: 40, photo: 320, stat: 144 },
    portrait: { pad: 72, h: 96,  eyebrow: 20, body: 24, logoH: 40, photo: 360, stat: 168 },
    story:    { pad: 96, h: 120, eyebrow: 22, body: 28, logoH: 52, photo: 520, stat: 220 },
    wide:     { pad: 56, h: 56,  eyebrow: 14, body: 16, logoH: 30, photo: 200, stat:  88 },
    yt:       { pad: 96, h: 96,  eyebrow: 20, body: 24, logoH: 44, photo: 360, stat: 168 },
  }[size];
  const isWide = size === "wide" || size === "yt";

  return (
    <div className={`canvas size-${size} bg-light`}>
      <div style={{ position:"absolute", inset:0, padding: L.pad, display:"flex", flexDirection:"column", justifyContent:"space-between" }}>
        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center" }}>
          <Logo variant="lockup" color="blue" height={L.logoH}/>
          <EditText fieldKey="kicker" fallback={defaults.kicker} as="div" className="meta-text"
            style={{ fontSize: L.eyebrow*0.9, color:"var(--eh-blue)", fontWeight: 500, padding:"6px 14px", border:"1px solid rgba(46,99,184,0.3)", borderRadius: 999 }}/>
        </div>

        <div style={{ display:"flex", gap: isWide ? 36 : 48, alignItems:"center" }}>
          <EditImage fieldKey="image" defaultSrc={defaults.image} label="Client portrait"
            style={{ width: L.photo, height: L.photo, borderRadius: 24, flexShrink: 0 }}/>
          <div style={{ flex: 1, display:"flex", flexDirection:"column", gap: isWide ? 14 : 22 }}>
            <EditText fieldKey="practice" fallback={defaults.practice} as="div" className="meta-text"
              style={{ fontSize: L.eyebrow, color:"var(--eh-muted)", fontWeight: 500 }}/>
            <EditText fieldKey="headline" fallback={defaults.headline} as="h1" multiline
              style={{ margin:0, fontSize: L.h, fontWeight: 700, lineHeight: 0.98, letterSpacing:"-0.03em", color:"var(--eh-navy)", textWrap:"balance" }}/>
            <div style={{ display:"flex", alignItems:"baseline", gap: 16, marginTop: 4 }}>
              <EditText fieldKey="stat" fallback={defaults.stat} as="div"
                style={{ fontSize: L.stat, fontWeight: 700, color:"var(--eh-blue)", letterSpacing:"-0.04em", lineHeight: 0.9 }}/>
              <EditText fieldKey="statLabel" fallback={defaults.statLabel} as="div" multiline
                style={{ fontSize: L.body, color:"var(--eh-muted)", fontWeight: 500, lineHeight: 1.25, maxWidth: "60%", letterSpacing:"-0.01em" }}/>
            </div>
          </div>
        </div>

        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center" }}>
          <div>
            <EditText fieldKey="name" fallback={defaults.name} as="div"
              style={{ fontSize: L.body, fontWeight: 700, color:"var(--eh-navy)", letterSpacing:"-0.01em" }}/>
            <EditText fieldKey="role" fallback={defaults.role} as="div"
              style={{ fontSize: L.body*0.85, color:"var(--eh-muted)" }}/>
          </div>
          <EditText fieldKey="cta" fallback={defaults.cta} as="div"
            style={{ fontSize: L.body*0.95, color:"var(--eh-blue)", fontWeight: 500 }}/>
        </div>
      </div>
    </div>
  );
}

function ProductMockup({ size, defaults }) {
  // Centered product device on gradient with framed shadow
  const L = {
    square:   { pad: 80,  h: 96,  eyebrow: 18, support: 22, logoH: 44, mockH: 540 },
    portrait: { pad: 80,  h: 110, eyebrow: 20, support: 24, logoH: 44, mockH: 700 },
    story:    { pad: 100, h: 132, eyebrow: 22, support: 28, logoH: 56, mockH: 1000 },
    wide:     { pad: 56,  h: 60,  eyebrow: 14, support: 16, logoH: 32, mockH: 380 },
    yt:       { pad: 100, h: 110, eyebrow: 20, support: 24, logoH: 44, mockH: 660 },
  }[size];
  const isWide = size === "wide" || size === "yt";

  const Mockup = (
    <EditImage fieldKey="mockup" defaultSrc={defaults.mockup} label="Product mockup"
      fit="contain"
      style={{ width:"100%", height: L.mockH, filter:"drop-shadow(0 40px 80px rgba(0,0,0,0.45))", background:"transparent", borderRadius: 0 }}/>
  );

  if (isWide) {
    return (
      <div className={`canvas size-${size} bg-deep`}>
        <div style={{ position:"absolute", inset:0, padding: L.pad, display:"grid", gridTemplateColumns:"1fr 1.2fr", gap: L.pad, alignItems:"center" }}>
          <div style={{ display:"flex", flexDirection:"column", gap: 22, color:"white" }}>
            <Logo variant="lockup" color="white" height={L.logoH}/>
            <EditText fieldKey="eyebrow" fallback={defaults.eyebrow} as="div" className="meta-text"
              style={{ fontSize: L.eyebrow, color:"rgba(255,255,255,0.65)", fontWeight: 500 }}/>
            <EditText fieldKey="headline" fallback={defaults.headline} as="h1" multiline
              style={{ margin:0, fontSize: L.h, fontWeight: 700, lineHeight: 0.98, letterSpacing:"-0.03em", color:"white", textWrap:"balance" }}/>
            <EditText fieldKey="support" fallback={defaults.support} as="p" multiline
              style={{ margin:0, fontSize: L.support, color:"rgba(255,255,255,0.75)", lineHeight: 1.4, letterSpacing:"-0.01em" }}/>
          </div>
          {Mockup}
        </div>
      </div>
    );
  }

  return (
    <div className={`canvas size-${size} bg-deep`}>
      <div style={{ position:"absolute", inset:0, padding: L.pad, display:"flex", flexDirection:"column", justifyContent:"space-between", color:"white" }}>
        <div style={{ display:"flex", flexDirection:"column", gap: 18 }}>
          <Logo variant="lockup" color="white" height={L.logoH}/>
          <EditText fieldKey="eyebrow" fallback={defaults.eyebrow} as="div" className="meta-text"
            style={{ fontSize: L.eyebrow, color:"rgba(255,255,255,0.65)", fontWeight: 500 }}/>
          <EditText fieldKey="headline" fallback={defaults.headline} as="h1" multiline
            style={{ margin:0, fontSize: L.h, fontWeight: 700, lineHeight: 0.98, letterSpacing:"-0.03em", color:"white", textWrap:"balance" }}/>
        </div>
        {Mockup}
        <EditText fieldKey="support" fallback={defaults.support} as="p" multiline
          style={{ margin:0, fontSize: L.support, color:"rgba(255,255,255,0.75)", lineHeight: 1.4, maxWidth: "85%", letterSpacing:"-0.01em" }}/>
      </div>
    </div>
  );
}

function WebinarBig({ size, defaults }) {
  // Large dedicated webinar/event template with prominent imagery
  const L = {
    square:   { pad: 72, title: 88,  body: 22, logoH: 44, photo: 360 },
    portrait: { pad: 72, title: 100, body: 24, logoH: 44, photo: 460 },
    story:    { pad: 96, title: 132, body: 28, logoH: 56, photo: 720 },
    wide:     { pad: 56, title: 56,  body: 16, logoH: 32, photo: 280 },
    yt:       { pad: 96, title: 100, body: 24, logoH: 44, photo: 460 },
  }[size];
  const isWide = size === "wide" || size === "yt";

  const Hero = (
    <EditImage fieldKey="image" defaultSrc={defaults.image} label="Speaker / event photo"
      style={{ width:"100%", height: L.photo, borderRadius: 24 }}/>
  );

  if (isWide) {
    return (
      <div className={`canvas size-${size} bg-soft`}>
        <div style={{ position:"absolute", inset:0, padding: L.pad, display:"grid", gridTemplateColumns:"1.1fr 1fr", gap: L.pad, alignItems:"stretch" }}>
          <EditImage fieldKey="image" defaultSrc={defaults.image} label="Speaker / event photo"
            style={{ height:"100%", borderRadius: 24 }}/>
          <div style={{ display:"flex", flexDirection:"column", justifyContent:"space-between" }}>
            <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center" }}>
              <Logo variant="lockup" color="blue" height={L.logoH}/>
              <EditText fieldKey="badge" fallback={defaults.badge} as="div" className="meta-text"
                style={{ fontSize: L.body*0.85, color:"var(--eh-blue)", fontWeight: 500, padding:"6px 14px", border:"1px solid rgba(46,99,184,0.3)", borderRadius: 999 }}/>
            </div>
            <div style={{ display:"flex", flexDirection:"column", gap: 18 }}>
              <EditText fieldKey="dateline" fallback={defaults.dateline} as="div" className="meta-text"
                style={{ fontSize: L.body, color:"var(--eh-muted)", fontWeight: 500 }}/>
              <EditText fieldKey="title" fallback={defaults.title} as="h1" multiline
                style={{ margin:0, fontSize: L.title, fontWeight: 700, lineHeight: 0.98, letterSpacing:"-0.035em", color:"var(--eh-navy)", textWrap:"balance" }}/>
              <EditText fieldKey="speaker" fallback={defaults.speaker} as="div"
                style={{ fontSize: L.body, color:"var(--eh-muted)", fontWeight: 500 }}/>
            </div>
            <div style={{
              alignSelf:"flex-start",
              padding:`${L.body*0.65}px ${L.body*1.4}px`, background:"var(--eh-navy)", color:"white",
              borderRadius: 999, fontSize: L.body, fontWeight: 700
            }}>
              <EditText fieldKey="cta" fallback={defaults.cta} as="span" style={{ color:"white" }}/>
              <span style={{ color:"white" }}> →</span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`canvas size-${size} bg-soft`}>
      <div style={{ position:"absolute", inset:0, padding: L.pad, display:"flex", flexDirection:"column", justifyContent:"space-between" }}>
        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center" }}>
          <Logo variant="lockup" color="blue" height={L.logoH}/>
          <EditText fieldKey="badge" fallback={defaults.badge} as="div" className="meta-text"
            style={{ fontSize: L.body*0.9, color:"var(--eh-blue)", fontWeight: 500, padding:"6px 14px", border:"1px solid rgba(46,99,184,0.3)", borderRadius: 999 }}/>
        </div>
        {Hero}
        <div style={{ display:"flex", flexDirection:"column", gap: 18 }}>
          <EditText fieldKey="dateline" fallback={defaults.dateline} as="div" className="meta-text"
            style={{ fontSize: L.body, color:"var(--eh-muted)", fontWeight: 500 }}/>
          <EditText fieldKey="title" fallback={defaults.title} as="h1" multiline
            style={{ margin:0, fontSize: L.title, fontWeight: 700, lineHeight: 0.98, letterSpacing:"-0.035em", color:"var(--eh-navy)", textWrap:"balance" }}/>
          <EditText fieldKey="speaker" fallback={defaults.speaker} as="div"
            style={{ fontSize: L.body, color:"var(--eh-muted)", fontWeight: 500 }}/>
        </div>
        <div style={{
          alignSelf:"flex-start",
          padding:`${L.body*0.65}px ${L.body*1.4}px`, background:"var(--eh-navy)", color:"white",
          borderRadius: 999, fontSize: L.body, fontWeight: 700
        }}>
          <EditText fieldKey="cta" fallback={defaults.cta} as="span" style={{ color:"white" }}/>
          <span style={{ color:"white" }}> →</span>
        </div>
      </div>
    </div>
  );
}

function MagazineCover({ size, defaults }) {
  // Editorial magazine-style cover: huge serif-sized headline overlapping image
  const L = {
    square:   { pad: 56, h: 168, eyebrow: 18, logoH: 40 },
    portrait: { pad: 56, h: 200, eyebrow: 20, logoH: 40 },
    story:    { pad: 72, h: 232, eyebrow: 22, logoH: 52 },
    wide:     { pad: 40, h: 96,  eyebrow: 14, logoH: 30 },
    yt:       { pad: 80, h: 188, eyebrow: 20, logoH: 44 },
  }[size];
  return (
    <div className={`canvas size-${size}`} style={{ background:"#F4F6FA" }}>
      <div style={{ position:"absolute", inset: L.pad, display:"flex", flexDirection:"column", justifyContent:"space-between" }}>
        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start" }}>
          <Logo variant="lockup" color="blue" height={L.logoH}/>
          <EditText fieldKey="issue" fallback={defaults.issue} as="div" className="meta-text"
            style={{ fontSize: L.eyebrow*0.9, color:"var(--eh-muted)", fontWeight: 500 }}/>
        </div>
        <EditText fieldKey="headline" fallback={defaults.headline} as="h1" multiline
          style={{
            margin:0, fontSize: L.h, fontWeight: 700, lineHeight: 0.88,
            letterSpacing:"-0.04em", color:"var(--eh-navy)", textWrap:"balance",
            position:"relative", zIndex: 2
          }}/>
        <EditImage fieldKey="image" defaultSrc={defaults.image} label="Cover photo"
          style={{ width:"100%", height: size==="wide" ? "45%" : "55%", borderRadius: 24, marginTop: -L.h*0.25 }}/>
        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"baseline" }}>
          <EditText fieldKey="kicker" fallback={defaults.kicker} as="div" className="meta-text"
            style={{ fontSize: L.eyebrow, color:"var(--eh-blue)", fontWeight: 500 }}/>
          <EditText fieldKey="byline" fallback={defaults.byline} as="div"
            style={{ fontSize: L.eyebrow, color:"var(--eh-muted)" }}/>
        </div>
      </div>
    </div>
  );
}

function ImageQuoteOverlay({ size, defaults }) {
  // Big quote over a photo
  const L = {
    square:   { pad: 80, q: 64, name: 22, logoH: 44 },
    portrait: { pad: 80, q: 72, name: 24, logoH: 44 },
    story:    { pad: 96, q: 92, name: 28, logoH: 56 },
    wide:     { pad: 56, q: 36, name: 14, logoH: 32 },
    yt:       { pad: 96, q: 64, name: 22, logoH: 44 },
  }[size];
  return (
    <div className={`canvas size-${size}`} style={{ background:"#0A1628" }}>
      <EditImage fieldKey="image" defaultSrc={defaults.image} label="Quote backdrop"
        style={{ position:"absolute", inset:0, borderRadius:0 }}/>
      <div style={{
        position:"absolute", inset:0,
        background:"linear-gradient(180deg, rgba(10,22,40,0.7) 0%, rgba(10,22,40,0.35) 50%, rgba(10,22,40,0.85) 100%)"
      }}/>
      <div style={{ position:"absolute", inset: L.pad, display:"flex", flexDirection:"column", justifyContent:"space-between", color:"white" }}>
        <Logo variant="lockup" color="white" height={L.logoH}/>
        <div>
          <div style={{ fontSize: L.q*1.4, color:"rgba(255,255,255,0.5)", fontWeight: 700, lineHeight: 0.5, marginBottom: 12, letterSpacing:"-0.05em" }}>“</div>
          <EditText fieldKey="quote" fallback={defaults.quote} as="p" multiline
            style={{ margin:0, fontSize: L.q, fontWeight: 500, color:"white", lineHeight: 1.15, letterSpacing:"-0.025em", textWrap:"balance", maxWidth:"95%" }}/>
        </div>
        <div>
          <EditText fieldKey="name" fallback={defaults.name} as="div"
            style={{ fontSize: L.name, fontWeight: 700, color:"white", letterSpacing:"-0.01em" }}/>
          <EditText fieldKey="role" fallback={defaults.role} as="div"
            style={{ fontSize: L.name*0.85, color:"rgba(255,255,255,0.65)" }}/>
        </div>
      </div>
    </div>
  );
}

function ImageStat({ size, defaults }) {
  // Image at top, huge stat below on light background
  const L = {
    square:   { pad: 72, num: 280, label: 28, logoH: 40, imgRatio: 0.42 },
    portrait: { pad: 72, num: 320, label: 32, logoH: 40, imgRatio: 0.45 },
    story:    { pad: 96, num: 440, label: 38, logoH: 52, imgRatio: 0.48 },
    wide:     { pad: 48, num: 160, label: 18, logoH: 30, imgRatio: 0.42 },
    yt:       { pad: 96, num: 320, label: 32, logoH: 44, imgRatio: 0.45 },
  }[size];
  return (
    <div className={`canvas size-${size} bg-light`}>
      <div style={{ position:"absolute", inset:0, display:"grid", gridTemplateRows: `${L.imgRatio*100}% ${(1-L.imgRatio)*100}%` }}>
        <EditImage fieldKey="image" defaultSrc={defaults.image} label="Header image" style={{ borderRadius: 0, height:"100%" }}/>
        <div style={{ padding: L.pad, display:"flex", flexDirection:"column", justifyContent:"space-between" }}>
          <div>
            <EditText fieldKey="stat" fallback={defaults.stat} as="div"
              style={{ fontSize: L.num, fontWeight: 700, color:"var(--eh-navy)", letterSpacing:"-0.05em", lineHeight: 0.85 }}/>
            <EditText fieldKey="label" fallback={defaults.label} as="div" multiline
              style={{ fontSize: L.label, fontWeight: 500, color:"var(--eh-navy)", marginTop: 18, lineHeight: 1.2, letterSpacing:"-0.02em", maxWidth:"85%", textWrap:"balance" }}/>
          </div>
          <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-end" }}>
            <Logo variant="lockup" color="blue" height={L.logoH}/>
            <EditText fieldKey="source" fallback={defaults.source} as="div" className="meta-text"
              style={{ fontSize: L.label*0.65, color:"var(--eh-muted)" }}/>
          </div>
        </div>
      </div>
    </div>
  );
}

function FeatureGrid({ size, defaults }) {
  // 4-up image grid with central headline overlay
  const L = {
    square:   { pad: 64, h: 88,  body: 18, logoH: 44 },
    portrait: { pad: 64, h: 100, body: 20, logoH: 44 },
    story:    { pad: 96, h: 132, body: 22, logoH: 56 },
    wide:     { pad: 48, h: 56,  body: 14, logoH: 32 },
    yt:       { pad: 80, h: 96,  body: 20, logoH: 44 },
  }[size];

  const isWide = size === "wide" || size === "yt";
  const cols = isWide ? 4 : 2;
  const items = defaults.items || [];

  return (
    <div className={`canvas size-${size} bg-light`}>
      <div style={{ position:"absolute", inset:0, padding: L.pad, display:"flex", flexDirection:"column", gap: L.pad*0.6 }}>
        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start" }}>
          <Logo variant="lockup" color="blue" height={L.logoH}/>
          <EditText fieldKey="kicker" fallback={defaults.kicker} as="div" className="meta-text"
            style={{ fontSize: L.body, color:"var(--eh-muted)", fontWeight: 500 }}/>
        </div>
        <EditText fieldKey="headline" fallback={defaults.headline} as="h1" multiline
          style={{ margin:0, fontSize: L.h, fontWeight: 700, lineHeight: 0.95, letterSpacing:"-0.03em", color:"var(--eh-navy)", textWrap:"balance", maxWidth: "85%" }}/>
        <div style={{ flex: 1, display:"grid", gridTemplateColumns: `repeat(${cols}, 1fr)`, gap: 18 }}>
          {items.map((it, i) => (
            <div key={i} style={{ position:"relative", borderRadius: 18, overflow:"hidden", background:"#E1E7F2" }}>
              <EditImage fieldKey={`image_${i}`} defaultSrc={it.image} label={`Photo ${i+1}`}
                style={{ position:"absolute", inset:0, borderRadius:0 }}/>
              <div style={{ position:"absolute", inset:0, background:"linear-gradient(180deg, rgba(10,22,40,0) 50%, rgba(10,22,40,0.85) 100%)"}}/>
              <div style={{ position:"absolute", left: 16, right: 16, bottom: 16, color:"white" }}>
                <EditText fieldKey={`label_${i}`} fallback={it.label} as="div"
                  style={{ fontSize: L.body, fontWeight: 700, color:"white", letterSpacing:"-0.01em" }}/>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function PortraitImageHero({ size, defaults }) {
  // Centered portrait image inside a navy gradient with name + tagline (single hero)
  const L = {
    square:   { pad: 80, h: 88,  eyebrow: 18, logoH: 44, photo: 380 },
    portrait: { pad: 80, h: 100, eyebrow: 20, logoH: 44, photo: 460 },
    story:    { pad: 96, h: 124, eyebrow: 24, logoH: 56, photo: 660 },
    wide:     { pad: 56, h: 56,  eyebrow: 14, logoH: 32, photo: 240 },
    yt:       { pad: 96, h: 96,  eyebrow: 20, logoH: 44, photo: 460 },
  }[size];
  const isWide = size === "wide" || size === "yt";

  if (isWide) {
    return (
      <div className={`canvas size-${size} bg-navy-gradient`}>
        <div style={{ position:"absolute", inset:0, padding: L.pad, display:"grid", gridTemplateColumns:"auto 1fr", gap: L.pad, alignItems:"center", color:"white" }}>
          <EditImage fieldKey="image" defaultSrc={defaults.image} label="Portrait" round
            style={{ width: L.photo, height: L.photo, flexShrink: 0 }}/>
          <div style={{ display:"flex", flexDirection:"column", gap: 18 }}>
            <Logo variant="lockup" color="white" height={L.logoH}/>
            <EditText fieldKey="eyebrow" fallback={defaults.eyebrow} as="div" className="meta-text"
              style={{ fontSize: L.eyebrow, color:"rgba(255,255,255,0.65)", fontWeight: 500 }}/>
            <EditText fieldKey="headline" fallback={defaults.headline} as="h1" multiline
              style={{ margin:0, fontSize: L.h, fontWeight: 700, lineHeight: 0.98, letterSpacing:"-0.03em", color:"white", textWrap:"balance" }}/>
            <EditText fieldKey="name" fallback={defaults.name} as="div"
              style={{ fontSize: L.eyebrow*1.1, color:"rgba(255,255,255,0.85)", fontWeight: 500 }}/>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`canvas size-${size} bg-navy-gradient`}>
      <div style={{ position:"absolute", inset:0, padding: L.pad, display:"flex", flexDirection:"column", justifyContent:"space-between", alignItems:"center", color:"white", textAlign:"center" }}>
        <Logo variant="lockup" color="white" height={L.logoH}/>
        <EditImage fieldKey="image" defaultSrc={defaults.image} label="Portrait" round
          style={{ width: L.photo, height: L.photo, flexShrink: 0 }}/>
        <div style={{ display:"flex", flexDirection:"column", gap: 14, alignItems:"center" }}>
          <EditText fieldKey="eyebrow" fallback={defaults.eyebrow} as="div" className="meta-text"
            style={{ fontSize: L.eyebrow, color:"rgba(255,255,255,0.7)", fontWeight: 500 }}/>
          <EditText fieldKey="headline" fallback={defaults.headline} as="h1" multiline
            style={{ margin:0, fontSize: L.h, fontWeight: 700, lineHeight: 0.98, letterSpacing:"-0.03em", color:"white", textWrap:"balance", maxWidth:"90%" }}/>
          <EditText fieldKey="name" fallback={defaults.name} as="div"
            style={{ fontSize: L.eyebrow*1.05, color:"rgba(255,255,255,0.8)", fontWeight: 500 }}/>
        </div>
      </div>
    </div>
  );
}

export {
  FullBleedImage, SplitImageText, SuccessStory, ProductMockup,
  WebinarBig, MagazineCover, ImageQuoteOverlay, ImageStat, FeatureGrid, PortraitImageHero,
};
